import { AlertProps, Box, IconButton, Snackbar, Typography, SnackbarProps, CircularProgress } from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import React, { useEffect } from 'react';
import { styles } from './Toast.styles';
import { circularProgressSizeProps } from '@opengov/capital-mui-theme';

const TOAST_TIME_OUT = 5000;

const ToastIconByColor = {
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoIcon
};

export type ToastProps = {
  title: string;
  body?: string;
  severity: AlertProps['severity'];
  onClose?: () => void;
  snackbarProps?: SnackbarProps;
  showLoader?: boolean;
  dataTest?: string;
};

export const Toast = ({
  title,
  body,
  severity = 'success',
  onClose,
  snackbarProps,
  showLoader,
  dataTest
}: ToastProps) => {
  const ToastIcon = ToastIconByColor[severity];

  useEffect(() => {
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', severity === 'error' ? 'assertive' : 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.style.cssText =
      'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap';
    document.body.appendChild(liveRegion);

    const timerId = setTimeout(() => {
      liveRegion.textContent = body ? `${title}. ${body}` : title;
    }, 50);

    return () => {
      clearTimeout(timerId);
      document.body.removeChild(liveRegion);
    };
  }, []);
  return (
    <Snackbar
      open={true}
      color={`${severity}Secondary`}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      autoHideDuration={TOAST_TIME_OUT}
      data-test={dataTest ? dataTest : 'toast'}
      sx={styles.toast}
      {...snackbarProps}
      onClose={onClose}
    >
      <Box sx={styles.toastContent}>
        {showLoader ? (
          <Box>
            <CircularProgress {...circularProgressSizeProps.small} />
          </Box>
        ) : (
          <ToastIcon sx={styles.toastIcon} data-test={`${severity}Icon`} />
        )}

        <Box sx={styles.textContainer}>
          <Typography variant="h4" component="div">
            {title}
          </Typography>
          {body ? (
            <Typography variant="bodySmall" sx={styles.body} component="div">
              {body}
            </Typography>
          ) : null}
        </Box>
        <IconButton sx={styles.closeButton} aria-label="close" onClick={onClose}>
          <CloseIcon sx={styles.closeIcon} />
        </IconButton>
      </Box>
    </Snackbar>
  );
};
